import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Inbox, Info, RefreshCw } from 'lucide-react';
import { feedbackBriefkasten } from '../api';

/**
 * Der Feedback-Briefkasten — was aus den Kundeninstallationen hereinkommt.
 *
 * ══ WAS HIER NICHT PASSIERT ════════════════════════════════════════════════
 *
 * Die Pakete werden **nicht geöffnet**. Der PC ist Briefkasten, nicht
 * Sammelstelle: Sichtung, Urteil und Zielfassung brauchen Katalog und
 * Punkt-Slugs, und das ist open.junixx-Wissen. Diese Ansicht sagt nur, **dass**
 * etwas da ist, von wem, und ob es schon abgeholt wurde.
 *
 * Wer den Inhalt braucht, holt ihn in der Sammelstelle ab. Ein „Öffnen"-Knopf
 * hier wäre der Anfang einer zweiten Fachlichkeit an einem zweiten Ort.
 *
 * ══ WARUM DIE LISTE OFT LEER IST ═══════════════════════════════════════════
 *
 * Der Weg aus open.junixx hierher setzt drei Dinge voraus: eine konfigurierte
 * Aktivierung, ein registriertes Agent-Token und den Schalter „automatisch
 * senden" in der Installation. Fehlt eines, bleibt der Rücklauf dort liegen —
 * ausdrücklich ohne Fehler, damit eine Installation ohne Aktivierung sich nicht
 * mit Fehlversuchen zumüllt. Eine leere Liste heisst also nicht, dass etwas
 * kaputt ist.
 */

const K = {
  karte: 'rounded-lg border border-slate-200 bg-surface',
  knopf: 'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium '
    + 'border border-slate-300 hover:bg-slate-50',
  th: 'px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500',
  td: 'px-3 py-2 text-sm align-top',
};

function datum(w: string | null): string {
  if (!w) return '—';
  return new Date(w).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
}

export default function FeedbackBriefkastenPanel() {
  const [nurOffen, setNurOffen] = useState(false);

  const abfrage = useQuery({
    queryKey: ['feedback-briefkasten', nurOffen],
    queryFn: () => feedbackBriefkasten.liste(nurOffen),
    retry: false,
  });

  const eintraege = abfrage.data?.eintraege ?? [];

  return (
    <div className="space-y-4">
      <div className="flex gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
        <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <div>
          Rücklaufpakete aus den Abnahmen der Kunden. Der PC nimmt sie an und hält sie
          fest — <strong>geöffnet werden sie in der Sammelstelle</strong>, nicht hier.
          Eine leere Liste ist kein Fehler: ohne Aktivierung und ohne den Schalter
          „automatisch senden" bleibt ein Rücklauf bewusst in der Installation liegen.
        </div>
      </div>

      <div className="flex items-center justify-between">
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={nurOffen}
            onChange={(e) => setNurOffen(e.target.checked)}
          />
          Nur noch nicht abgeholte
        </label>
        <button
          type="button"
          className={K.knopf}
          onClick={() => abfrage.refetch()}
          disabled={abfrage.isFetching}
        >
          <RefreshCw className={`h-4 w-4 ${abfrage.isFetching ? 'animate-spin' : ''}`} aria-hidden />
          Neu laden
        </button>
      </div>

      <div className={`${K.karte} overflow-x-auto`}>
        <table className="w-full">
          <thead className="border-b border-slate-200">
            <tr>
              <th className={K.th}>Eingegangen</th>
              <th className={K.th}>Kunde</th>
              <th className={K.th}>Instanz</th>
              <th className={K.th}>Maske / Kampagne</th>
              <th className={K.th}>Antworten</th>
              <th className={K.th}>Abgeholt</th>
            </tr>
          </thead>
          <tbody>
            {eintraege.map(e => (
              <tr key={e.id} className="border-b border-slate-100">
                <td className={K.td}>{datum(e.receivedAt)}</td>
                <td className={K.td}>{e.customer.companyName}</td>
                <td className={K.td}>
                  <Link
                    to={`/instances/${e.instance.id}`}
                    className="hover:underline"
                  >
                    {e.instance.stackName ?? e.instance.subdomain ?? e.instance.id.slice(0, 8)}
                  </Link>
                </td>
                <td className={K.td}>
                  {/* Anzeigetext aus einer fremden Installation — serverseitig auf
                      200 Zeichen gekürzt. Er steht in einer Liste, nicht in einer
                      Entscheidung. */}
                  <div>{e.kampagneName ?? '—'}</div>
                  <div className="font-mono text-xs text-slate-500">
                    {e.surfaceId ?? '—'}
                    {e.fassungNr != null && ` · v${e.fassungNr}`}
                  </div>
                </td>
                <td className={K.td}>{e.antwortenAnz ?? '—'}</td>
                <td className={K.td}>
                  {e.fetchedAt
                    ? <span className="text-slate-500">{datum(e.fetchedAt)}</span>
                    : <span className="text-emerald-700">liegt noch</span>}
                </td>
              </tr>
            ))}
            {eintraege.length === 0 && !abfrage.isFetching && (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center text-sm text-slate-500">
                  <Inbox className="mx-auto mb-2 h-6 w-6 opacity-40" aria-hidden />
                  Der Briefkasten ist leer.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-500">
        „Abgeholt" heisst nur, dass die Sammelstelle den Eintrag gelesen hat — gelöscht
        wird hier nichts. Dass ein Paket wirklich eingespielt wurde, weiss der PC nicht,
        und er tut nicht so, als wüsste er es.
      </p>
    </div>
  );
}
